/**
 * Error Screen - Telas de Erro
 *
 * Displays different error states following Figma design:
 * - retry-later: "We couldn't process this payment" (keep fields)
 * - dont-retry: "This payment could not be processed" (clear fields except name)
 * - revalidate-input: "Card details didn't match" (keep fields)
 * - unexpected: "We had an unexpected error" (reload iframe)
 * - max-attempts: "Maximum payment attempts reached" (close/new request)
 */

import type { ErrorScreenType } from '@/types/voice-dtmf.types';
import styles from './ErrorScreen.module.css';

interface ErrorScreenProps {
  errorType: ErrorScreenType;
  errorMessage?: string;
  onTryAgain: () => void;
  onAction?: () => void;
}

const WarningIcon = () => (
  <svg
    width="80"
    height="73"
    viewBox="0 0 80 73"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      opacity="0.1"
      fillRule="evenodd"
      clipRule="evenodd"
      d="M32.651 9.71329C36.8697 2.45853 47.4236 2.45852 51.6424 9.71329L78.5167 55.9277C82.7331 63.1784 77.4585 72.2388 69.021 72.2388H15.2724C6.8349 72.2388 1.5603 63.1784 5.7767 55.9277L32.651 9.71329Z"
      fill="#06A4F3"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M28.4108 5.63491C32.7799 -1.8783 43.7087 -1.87831 48.0777 5.63491L74.952 51.8493C79.3201 59.3608 73.8545 68.7434 65.1186 68.7434H11.37C2.63403 68.7434 -2.8315 59.3608 1.53654 51.8493L28.4108 5.63491ZM47.4022 6.024C43.3338 -0.972317 33.1548 -0.972315 29.0863 6.024L2.21204 52.2384C-1.85273 59.2284 3.23094 67.9666 11.37 67.9666H65.1186C73.2576 67.9666 78.3413 59.2284 74.2765 52.2384L47.4022 6.024ZM35.1141 27.5694C35.1141 25.852 36.5171 24.462 38.2451 24.462C39.973 24.462 41.376 25.852 41.376 27.5694V41.1645C41.376 42.8818 39.973 44.2719 38.2451 44.2719C36.5171 44.2719 35.1141 42.8818 35.1141 41.1645V27.5694ZM35.1141 52.0406C35.1141 50.3233 36.5171 48.9332 38.2451 48.9332C39.973 48.9332 41.376 50.3233 41.376 52.0406C41.376 53.7579 39.973 55.148 38.2451 55.148C36.5171 55.148 35.1141 53.7579 35.1141 52.0406Z"
      fill="#06A4F3"
    />
  </svg>
);

const SadIcon = () => (
  <svg
    width="278"
    height="216"
    viewBox="0 0 278 216"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect
      opacity="0.1"
      x="11.4346"
      y="49.7256"
      width="239"
      height="167"
      rx="7"
      transform="rotate(-10.1902 11.4346 49.7256)"
      fill="#06A4F3"
      stroke="#06A4F3"
      strokeWidth="2"
    />
    <rect
      x="1.16114"
      y="43.444"
      width="239"
      height="167"
      rx="7"
      transform="rotate(-10.1902 1.16114 43.444)"
      stroke="#06A4F3"
      strokeWidth="2"
    />
    <line
      x1="2.83065"
      y1="58.3843"
      x2="240.029"
      y2="15.7476"
      stroke="#06A4F3"
      strokeWidth="2"
    />
    <circle
      cx="12.2417"
      cy="48.5644"
      r="1.5"
      transform="rotate(-10.1902 12.2417 48.5644)"
      fill="#06A4F3"
      stroke="#06A4F3"
    />
    <circle
      cx="20.1155"
      cy="47.1492"
      r="1.5"
      transform="rotate(-10.1902 20.1155 47.1492)"
      fill="#06A4F3"
      stroke="#06A4F3"
    />
    <circle
      cx="27.9895"
      cy="45.7338"
      r="1.5"
      transform="rotate(-10.1902 27.9895 45.7338)"
      fill="#06A4F3"
      stroke="#06A4F3"
    />
    <circle
      cx="92.7369"
      cy="95.057"
      r="7.5"
      transform="rotate(-10.1902 92.7369 95.057)"
      fill="#06A4F3"
      stroke="#06A4F3"
    />
    <circle
      cx="168.522"
      cy="81.4344"
      r="7.5"
      transform="rotate(-10.1902 168.522 81.4344)"
      fill="#06A4F3"
      stroke="#06A4F3"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M120.033 141.665C119.465 143.786 117.291 145.055 115.162 144.501C113.024 143.945 111.742 141.761 112.298 139.623L116.169 140.63C112.298 139.623 112.299 139.621 112.299 139.619L112.3 139.614L112.303 139.603L112.31 139.578C112.315 139.56 112.321 139.538 112.328 139.513C112.342 139.462 112.36 139.399 112.383 139.323C112.429 139.17 112.493 138.968 112.578 138.721C112.749 138.228 113.005 137.556 113.37 136.756C114.097 135.16 115.268 133.029 117.073 130.792C120.727 126.264 126.878 121.433 136.783 119.652C146.688 117.872 154.137 120.258 159.138 123.231C161.609 124.699 163.449 126.289 164.687 127.532C165.308 128.155 165.782 128.696 166.113 129.098C166.279 129.3 166.41 129.467 166.505 129.594C166.553 129.657 166.593 129.711 166.623 129.753C166.639 129.774 166.652 129.792 166.663 129.808L166.678 129.829L166.684 129.838L166.687 129.842C166.689 129.844 166.69 129.846 163.412 132.138L166.69 129.846C167.956 131.656 167.515 134.15 165.704 135.416C163.902 136.677 161.421 136.245 160.15 134.454C160.147 134.449 160.138 134.438 160.125 134.421C160.092 134.377 160.029 134.296 159.936 134.182C159.75 133.956 159.445 133.605 159.019 133.177C158.165 132.32 156.846 131.175 155.051 130.108C151.521 128.01 146.009 126.122 138.198 127.526C130.387 128.93 125.878 132.62 123.299 135.816C121.988 137.441 121.151 138.974 120.649 140.075C120.399 140.623 120.235 141.059 120.139 141.336C120.091 141.474 120.06 141.573 120.045 141.625C120.038 141.646 120.035 141.659 120.033 141.665ZM160.14 134.439C160.139 134.437 160.138 134.436 160.137 134.435C160.137 134.434 160.136 134.434 160.136 134.434C160.136 134.434 160.136 134.434 160.136 134.434L160.137 134.435L160.139 134.437L160.14 134.439Z"
      fill="#3E1089"
    />
    <path
      d="M160.137 134.435C160.138 134.436 160.139 134.437 160.14 134.439L160.139 134.437L160.137 134.435ZM160.137 134.435C160.137 134.434 160.136 134.434 160.136 134.434C160.136 134.434 160.136 134.434 160.136 134.434L160.137 134.435ZM115.162 144.501C117.291 145.055 119.465 143.786 120.033 141.665C120.035 141.659 120.038 141.646 120.045 141.625C120.06 141.573 120.091 141.474 120.139 141.336C120.235 141.059 120.399 140.623 120.649 140.075C121.151 138.974 121.988 137.441 123.299 135.816C125.878 132.62 130.387 128.93 138.198 127.526C146.009 126.122 151.521 128.01 155.051 130.108C156.846 131.175 158.165 132.32 159.019 133.177C159.445 133.605 159.75 133.956 159.936 134.182C160.029 134.296 160.092 134.377 160.125 134.421C160.138 134.438 160.147 134.449 160.15 134.454C161.421 136.245 163.902 136.677 165.704 135.416C167.515 134.15 167.956 131.656 166.69 129.846L163.412 132.138C166.69 129.846 166.689 129.844 166.687 129.842L166.684 129.838L166.678 129.829L166.663 129.808C166.652 129.792 166.639 129.774 166.623 129.753C166.593 129.711 166.553 129.657 166.505 129.594C166.41 129.467 166.279 129.3 166.113 129.098C165.782 128.696 165.308 128.155 164.687 127.532C163.449 126.289 161.609 124.699 159.138 123.231C154.137 120.258 146.688 117.872 136.783 119.652C126.878 121.433 120.727 126.264 117.073 130.792C115.268 133.029 114.097 135.16 113.37 136.756C113.005 137.556 112.749 138.228 112.578 138.721C112.493 138.968 112.429 139.17 112.383 139.323C112.36 139.399 112.342 139.462 112.328 139.513C112.321 139.538 112.315 139.56 112.31 139.578L112.303 139.603L112.3 139.614L112.299 139.619C112.299 139.621 112.298 139.623 116.169 140.63L112.298 139.623C111.742 141.761 113.024 143.945 115.162 144.501Z"
      stroke="#06A4F3"
      strokeWidth="8"
      strokeLinecap="round"
    />
  </svg>
);

const BlockedCardIcon = () => (
  <svg
    width="194"
    height="194"
    viewBox="0 0 194 194"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <g opacity="0.1">
      <mask id="path-1-inside-1_33_816" fill="white">
        <path d="M97 0C150.572 0 194 43.4284 194 97C194 150.572 150.572 194 97 194C43.4284 194 0 150.572 0 97C0 43.4284 43.4284 0 97 0ZM35.9863 143.977C50.0661 162.236 72.159 174 97 174C139.526 174 174 139.526 174 97C174 86.6035 171.939 76.6886 168.204 67.6406L35.9863 143.977ZM97 20C54.4741 20 20 54.4741 20 97C20 107.522 22.1116 117.551 25.9326 126.688L158.229 50.3057C144.16 31.8851 121.969 20 97 20Z" />
      </mask>
      <path
        d="M97 0C150.572 0 194 43.4284 194 97C194 150.572 150.572 194 97 194C43.4284 194 0 150.572 0 97C0 43.4284 43.4284 0 97 0ZM35.9863 143.977C50.0661 162.236 72.159 174 97 174C139.526 174 174 139.526 174 97C174 86.6035 171.939 76.6886 168.204 67.6406L35.9863 143.977ZM97 20C54.4741 20 20 54.4741 20 97C20 107.522 22.1116 117.551 25.9326 126.688L158.229 50.3057C144.16 31.8851 121.969 20 97 20Z"
        fill="#06A4F3"
      />
      <path
        d="M35.9863 143.977L25.9863 126.656L6.18909 138.086L20.148 156.189L35.9863 143.977ZM168.204 67.6406L186.691 60.0096L177.979 38.9032L158.204 50.3201L168.204 67.6406ZM25.9326 126.688L7.48131 134.404L16.2504 155.372L35.9326 144.008L25.9326 126.688ZM158.229 50.3057L168.23 67.6262L187.935 56.2491L174.124 38.166L158.229 50.3057ZM97 0V20C139.526 20 174 54.4741 174 97H194H214C214 32.3827 161.617 -20 97 -20V0ZM194 97H174C174 139.526 139.526 174 97 174V194V214C161.617 214 214 161.617 214 97H194ZM97 194V174C54.4741 174 20 139.526 20 97H0H-20C-20 161.617 32.3827 214 97 214V194ZM0 97H20C20 54.4741 54.4741 20 97 20V0V-20C32.3827 -20 -20 32.3827 -20 97H0ZM35.9863 143.977L20.148 156.189C37.851 179.148 65.695 194 97 194V174V154C78.623 154 62.2811 145.325 51.8247 131.764L35.9863 143.977ZM97 174V194C150.572 194 194 150.572 194 97H174H154C154 128.48 128.48 154 97 154V174ZM174 97H194C194 83.939 191.407 71.4345 186.691 60.0096L168.204 67.6406L149.717 75.2716C152.471 81.9426 154 89.268 154 97H174ZM168.204 67.6406L158.204 50.3201L25.9863 126.656L35.9863 143.977L45.9863 161.297L178.204 84.9611L168.204 67.6406ZM97 20V0C43.4284 0 0 43.4284 0 97H20H40C40 65.5198 65.5198 40 97 40V20ZM20 97H0C0 110.22 2.6568 122.869 7.48131 134.404L25.9326 126.688L44.3839 118.971C41.5665 112.234 40 104.825 40 97H20ZM25.9326 126.688L35.9326 144.008L168.23 67.6262L158.229 50.3057L148.229 32.9852L15.9326 109.367L25.9326 126.688ZM158.229 50.3057L174.124 38.166C156.434 15.0056 128.467 0 97 0V20V40C115.471 40 131.886 48.7646 142.335 62.4453L158.229 50.3057Z"
        fill="#06A4F3"
        mask="url(#path-1-inside-1_33_816)"
      />
    </g>
    <rect
      x="24.9314"
      y="63.3231"
      width="133.551"
      height="93.0546"
      rx="7"
      transform="rotate(-10.1902 24.9314 63.3231)"
      stroke="#06A4F3"
      strokeWidth="2"
    />
    <rect
      x="29.4927"
      y="86.3896"
      width="132.964"
      height="18.0074"
      transform="rotate(-10.19 29.4927 86.3896)"
      fill="#06A4F3"
      stroke="#06A4F3"
    />
    <rect
      x="134.527"
      y="94.4154"
      width="23.6159"
      height="6.3798"
      transform="rotate(-10.19 134.527 94.4154)"
      fill="#06A4F3"
      stroke="#06A4F3"
    />
  </svg>
);

interface ErrorConfig {
  icon: React.ReactNode;
  title: string;
  message: string;
  buttonText: string;
}

const ERROR_CONFIGS: Record<ErrorScreenType, ErrorConfig> = {
  'retry-later': {
    icon: <WarningIcon />,
    title: "We couldn't process this payment",
    message: 'Something went wrong on our side. Please, try again',
    buttonText: 'Try Again',
  },
  'dont-retry': {
    icon: <WarningIcon />,
    title: 'This payment could not be processed',
    message: 'Please try again using a different payment method',
    buttonText: 'Try Again',
  },
  'revalidate-input': {
    icon: <WarningIcon />,
    title: "Card details didn't match",
    message: 'Please review card details and try again',
    buttonText: 'Try Again',
  },
  unexpected: {
    icon: <SadIcon />,
    title: 'We had an unexpected error',
    message: 'Try again in a few minutes',
    buttonText: 'Try Again',
  },
  'max-attempts': {
    icon: <BlockedCardIcon />,
    title: 'Maximum payment attempts reached',
    message:
      "You've reached the limit of 3 attempts for this payment. You will need to generate a new payment request.",
    buttonText: 'Cancel payment',
  },
  'session-expired': {
    icon: <WarningIcon />,
    title: 'Your session has expired',
    message:
      'This payment session is no longer valid. Please generate a new payment request.',
    buttonText: 'Close',
  },
  'sse-disconnected': {
    icon: <WarningIcon />,
    title: 'Connection lost',
    message:
      'We lost the connection to the server. Please refresh the page and try again.',
    buttonText: 'Refresh',
  },
  'card-has-mandates': {
    icon: <WarningIcon />,
    title: 'Card cannot be deleted',
    message:
      'This card is being used as a recurring payment method and cannot be deleted.',
    buttonText: 'Go Back',
  },
};

const DEFAULT_ERROR_CONFIG: ErrorConfig = {
  icon: <WarningIcon />,
  title: 'Error',
  message: 'An error occurred.',
  buttonText: 'Try Again',
};

export const ErrorScreen = ({
  errorType,
  errorMessage,
  onTryAgain,
  onAction,
}: ErrorScreenProps) => {
  const config = ERROR_CONFIGS[errorType] || DEFAULT_ERROR_CONFIG;
  const handleClick = onAction ?? onTryAgain;

  return (
    <div className={styles.container}>
      {/* Error Icon */}
      <div className={styles.iconContainer}>{config.icon}</div>

      {/* Title */}
      <h2 className={styles.title}>{config.title}</h2>

      {/* Message */}
      <p className={styles.message}>{errorMessage ?? config.message}</p>

      {/* Action Button */}
      <button
        type="button"
        className={styles.retryButton}
        onClick={handleClick}
      >
        {config.buttonText}
      </button>
    </div>
  );
};
